/**
 * Gate 6 — Pattern Re-derivation
 *
 * Detects template-matching without first-principle re-derivation
 * on inputs marked as novel. When decisionClass or rawOutput
 * signals a novel scenario, the output must show derivation
 * reasoning rather than citing a stock pattern or protocol.
 *
 * Trip routes to abstention with reason='pattern_re_derivation_skipped'.
 */

const TEMPLATE_MARKERS = [
  'as we have seen', 'following the standard approach', 'as per protocol',
  'standard procedure', 'typically in these cases', 'as is common',
  'by convention', 'as usual', 'as is standard', 'per our usual practice',
  'following established procedure', 'per standard', 'as is customary',
  'as is typical', 'as normally done', 'as per the playbook',
  'standard recommendation', 'per the template', 'following the template',
];

const NOVEL_INDICATORS = [
  'novel', 'unprecedented', 'first time', 'new situation', 'unique case',
  'no prior', 'no historical', 'unusual', 'atypical', 'edge case',
  'never before',
];

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [_config]
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkPatternReDerivation(envelope, _config = {}) {
  const text = (envelope.rawOutput || '').toLowerCase();

  if (!text) {
    return { gateName: 'pattern_re_derivation', verdict: 'pass', reason: null, payload: null };
  }

  const isNovelContext = NOVEL_INDICATORS.some(n => text.includes(n));

  if (!isNovelContext) {
    return { gateName: 'pattern_re_derivation', verdict: 'pass', reason: null, payload: { novelContextDetected: false } };
  }

  const templateHits = TEMPLATE_MARKERS.filter(m => text.includes(m));

  if (templateHits.length > 0) {
    return {
      gateName: 'pattern_re_derivation',
      verdict: 'trip',
      reason: 'pattern_re_derivation_skipped',
      payload: { templateHits, novelContextDetected: true },
    };
  }

  return {
    gateName: 'pattern_re_derivation',
    verdict: 'pass',
    reason: null,
    payload: { novelContextDetected: true, templateHits: [] },
  };
}
